import objectFitImages from 'object-fit-images';
import ImageBuddy from 'imagebuddy';

import ThemeWelcome from './components/testing/theme-welcome';
import SiteHeader from './components/site-header';
import BannerVideo from './components/banner-video';
import Modal from './components/modal';

import PostsListBlog from './components/posts-list/posts-list-blog';
import PostsListSearch from './components/posts-list/posts-list-search';

import { processExternalLinks } from './util/general-util';
import ScrollWatcher from './util/scroll-watcher';
import SiteEvents, { SiteEventNames } from './util/site-events';
import { loadIntersectionObserver } from './util/load-dependencies';
import { createSingleUseObserver } from './util/intersection-observer';
import { initHelloBar } from './util/hello-bar';
import ModalDefault from './components/modal-default';
import EqualHeight from './util/equal-height';
import AjaxLoadMoreFocus from './components/ajax-load-more-focus';
import LogosSlider from './components/logos-silder';

import TestimonialsSlider from './components/testimonials-slider';
import PostsSlider from './components/post-slider';

import StatsSlider from './components/stats-slider';
import SocialShare from './components/social-share';
import Accordion from './components/accordion';
import SideTabs from './components/side-tabs';
import SideTabsSlider from './components/side-tabs-slider';
import SideTabsFormModal from './components/side-tabs-form-modal';
import NewsletterSubmit from './components/newsletter-submit';
import MediaContentSlider from './components/media-content-slider';
import MediaContentCaptions from './components/media-content-captions';
import MediaContentTranscript from './components/media-content-transcript';
import MediaContentVideoFullscreen from './components/media-content-video-fullscreen';
import ImageGallerySlider from './components/image-gallery-slider';
import AccordionImage from './blocks/accordion-image';

import ResourceTemplate from './templates/resource';
import EventTemplate from './templates/event';
import InteractiveMap from './components/InteractiveMap';
import BannerSlider from './components/banner-slider';
import BannerStatsSlider from './components/banner-stats-slider';
import PostTabs from './components/post-tabs';
import LoadMorePosts from './components/load-more-posts';
import StickyContentCards from './components/sticky-content-cards';

class App {
	constructor() {
		this.instances = {
			components: {
				accordion: [],
				stickyContentCards: [],
				bannerVideo: null,
				equalHeight: [],
				ajaxLoadMoreFocus: null,
				imageGallerySlider: [],
				logosSlider: [],
				mediaContentSlider: [],
				mediaContentCaptions: [],
				mediaContentTranscript: [],
				mediaContentVideoFullscreen: [],
				modal: [],
				modalVideoPopup: [],
				newsletterSubmit: [],
				postsListBlog: [],
				postsListSearch: [],
				siteHeader: null,
				socialShare: null,
				statsSlider: [],
				testimonialsSlider: [],
				postsSlider: [],
				sideTabsSlider: [],
				sideTabsFormModal: [],
				themeWelcome: null
			},
			templates: {
				resourceTemplate: null,
				eventTemplate: null
			},
			blocks: {
				accordionImage: [],
				sideTabs: []
			},
			scrollWatcher: null,
			imageBuddy: null
		};

		this.init();
		this.initComponents();
		this.initTemplates();
		this.initBlocks();
		this.initSocialShare();
	}

	init() {
		processExternalLinks({
			target: '_blank',
			rel: 'noopener'
		});

		Modal.setDefaults({
			closeDuration: 400,
			closeButtonContent: `
				<svg xmlns="http://www.w3.org/2000/svg" class="modal__close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<line x1="18" y1="6" x2="6" y2="18"></line>
					<line x1="6" y1="6" x2="18" y2="18"></line>
				</svg>
			`.trim()
		});

		this.scrollWatcher = new ScrollWatcher((params) => {
			ScrollWatcher.defaultCallback(params, 100);
		});

		// initialize ImageBuddy and setup events
		this.instances.imageBuddy = new ImageBuddy({
			lazyLoad: true
			// debug: true
		});

		ImageBuddy.on('image-loaded', (imgEl) => {
			objectFitImages(imgEl);
		});

		SiteEvents.subscribe(SiteEventNames.IMAGEBUDDY_TRIGGER_UPDATE, (opts) => {
			this.instances.imageBuddy.update(opts || {});
		});

		initHelloBar((hbEl) => {
			document.body.style.marginTop = `${hbEl.offsetHeight}px`;

			const siteHeaderEl = document.querySelector('.site-header');

			if (siteHeaderEl) {
				siteHeaderEl.style.marginTop = `${hbEl.offsetHeight}px`;
			}
		});

		// ObjectFitImages: only act upon images that have a valid src attribute
		// Note: use the 'object-fit-polyfill' SCSS mixin to create the necessary 'font-family' attribute
		objectFitImages('img:not([src^="data:"])');
	}

	async initComponents() {
		// theme welcome
		// TODO: remove during development
		const themeWelcomeEl = document.querySelector('.theme-welcome');

		if (themeWelcomeEl) {
			this.instances.themeWelcome = new ThemeWelcome(themeWelcomeEl);
		}

		// site header
		const siteHeaderEl = document.querySelector('.site-header');

		if (siteHeaderEl) {
			this.instances.components.siteHeader = new SiteHeader(siteHeaderEl);
		}

		// focus on archives
		this.instances.components.ajaxLoadMoreFocus = new AjaxLoadMoreFocus();

		// newsletter signup
		Array.from(document.querySelectorAll('.newsletter-signup')).forEach((newsletterSignupEl) => {
			this.instances.components.newsletterSubmit.push(new NewsletterSubmit(newsletterSignupEl));
		});

		// banner cover video
		const bannerCoverVideoEl = document.querySelector('.banner__cover-video');

		if (bannerCoverVideoEl) {
			this.instances.components.bannerVideo = new BannerVideo(bannerCoverVideoEl);
		}

		// posts list - blog
		Array.from(document.querySelectorAll('.posts-list--blog')).forEach((postsListBlogEl) => {
			this.instances.components.postsListBlog.push(new PostsListBlog(postsListBlogEl));
		});

		// posts list - search
		Array.from(document.querySelectorAll('.posts-list--search')).forEach(
			(postsListSearchEl) => {
				this.instances.components.postsListSearch.push(
					new PostsListSearch(postsListSearchEl)
				);
			}
		);

		// Default Modal
		Array.from(document.querySelectorAll('.js__modal--default')).forEach(
			(modalDefaultEl) => {
				this.instances.components.modal.push(new ModalDefault(modalDefaultEl));
			}
		);

		// Side Tabs Form Modal
		Array.from(document.querySelectorAll('[data-form-trigger]')).forEach(

			(triggerEl) => {
				this.instances.components.sideTabsFormModal.push(
					new SideTabsFormModal(triggerEl)
				);
			}
		);

		// Testimonials Slider
		Array.from(document.querySelectorAll('.block-testimonials')).forEach(
			(testimonialsBlock) => {
				this.slider = testimonialsBlock.querySelector( '.block-testimonials__testimonials-slider' );
				this.prevButton = testimonialsBlock.querySelector( '.custom-prev-button' );
				this.nextButton = testimonialsBlock.querySelector( '.custom-next-button' );
				if (this.slider && this.prevButton && this.nextButton) {
					this.instances.components.testimonialsSlider.push(
						new TestimonialsSlider( this.slider, this.prevButton, this.nextButton )
					);
				}
			}
		);

		// Posts Slider
		Array.from(document.querySelectorAll('.js-posts-slider')).forEach(
			(sliderWrapper) => {
				this.instances.components.postsSlider.push(
					new PostsSlider(sliderWrapper)
				);
			}
		);

		// Image Gallery Slider
		Array.from(document.querySelectorAll('.block-image-gallery')).forEach(

			(imageGalleryBlock) => {
				this.slider = imageGalleryBlock.querySelector('.block-image-gallery__slider');

				// tiny-slider's click/keydown handling walks UP the DOM from the event
				// target looking for this exact element — if it's wrapped in a real
				// <button> (for keyboard support), the button (not the icon div inside
				// it) is what actually receives focus/Enter, so that's what we need to
				// pass here. closest('button') finds it; falls back to the div itself
				// when there's no wrapping button.
				const prevEl = imageGalleryBlock.querySelector('.custom-prev-button');
				const nextEl = imageGalleryBlock.querySelector('.custom-next-button');
				this.prevButton = prevEl?.closest('button') || prevEl;
				this.nextButton = nextEl?.closest('button') || nextEl;
				this.nav = imageGalleryBlock.querySelector('.custom-nav-container-image-gallery');
				if (this.slider) {
					this.instances.components.imageGallerySlider.push(
						new ImageGallerySlider(this.slider, this.prevButton, this.nextButton, this.nav)
					);
				}
			}
		);

		// Side Tabs Slider
		Array.from(document.querySelectorAll('.block-side-tabs__tab-content-slider-wrapper')).forEach(

			(sideTabsSliderBlock) => {
				this.slider = sideTabsSliderBlock.querySelector('.block-side-tabs__tab-content-slider');
				this.prevButton = sideTabsSliderBlock.querySelector('.custom-prev-button');
				this.nextButton = sideTabsSliderBlock.querySelector('.custom-next-button');
				this.nav = sideTabsSliderBlock.querySelector('.custom-nav-container-side-tabs');
				if (this.slider) {
					this.instances.components.sideTabsSlider.push(
						new SideTabsSlider(this.slider, this.prevButton, this.nextButton, this.nav)
					);
				}
			}
		);

		// Logos Slider
		Array.from(document.querySelectorAll('.block-logos')).forEach(

            (logosBlock) => {
                const prevButton = logosBlock.querySelector('.custom-prev-button');
                const nextButton = logosBlock.querySelector('.custom-next-button');
                const sliders = logosBlock.querySelectorAll('.block-logos__logos-slider');
                sliders.forEach((slider) => {
                    const isDesktopSlider = !!slider.closest('.block-logos__slider-desktop');
                    this.prevButton = isDesktopSlider ? prevButton : null;
                    this.nextButton = isDesktopSlider ? nextButton : null;
                    this.instances.components.logosSlider.push(
                        new LogosSlider(slider, this.prevButton, this.nextButton)
                    );
                });
            }
        );

		// Media Content Slider
		Array.from(document.querySelectorAll('.block-media-content__slider-wrapper')).forEach(

			(mediaContentSlider) => {
				this.slider = mediaContentSlider.querySelector('.block-media-content__slider');
				this.prevButton = mediaContentSlider.querySelector('.custom-prev-button');
				this.nextButton = mediaContentSlider.querySelector('.custom-next-button');
				this.nav = mediaContentSlider.querySelector('.custom-nav-container-slider');
				if (this.slider) {
					this.instances.components.mediaContentSlider.push(
						new MediaContentSlider(this.slider,this.prevButton, this.nextButton, this.nav)
					);
				}
			}
		);

		// Media Content Captions
		Array.from(document.querySelectorAll('.js-media-content-captions')).forEach(
			(captionsEl) => {
				this.instances.components.mediaContentCaptions.push(
					new MediaContentCaptions(captionsEl)
				);
			}
		);

		// Media Content Timed Transcript
		Array.from(document.querySelectorAll('.js-media-content-transcript')).forEach(
			(transcriptEl) => {
				this.instances.components.mediaContentTranscript.push(
					new MediaContentTranscript(transcriptEl)
				);
			}
		);

		// Media Content Video Fullscreen
		Array.from(document.querySelectorAll('.block-media-content .video-inline-wrapper')).forEach(
			(wrapperEl) => {
				this.instances.components.mediaContentVideoFullscreen.push(
					new MediaContentVideoFullscreen(wrapperEl)
				);
			}
		);

		// Social Share
		const socialShareEl = document.querySelector('.tmpl-single');

		if (socialShareEl) {
			this.instances.components.socialShare = new SocialShare(socialShareEl);
		}

		// accordion
		Array.from(document.querySelectorAll('.accordion-container')).forEach((accordionEl) => {
			this.instances.components.accordion.push(new Accordion(accordionEl));
		});

		// Stats Slider
		Array.from(document.querySelectorAll('.block-stats')).forEach(
			(statsBlock) => {
				this.slider = statsBlock.querySelector('.stats__stat-slider');
				this.nav = statsBlock.querySelector('.custom-nav-container-stats');
				if (this.slider) {
					this.instances.components.statsSlider.push(
						new StatsSlider(this.slider, this.nav)
					);
				}
			}
		);

		// Equalize Height
		Array.from(document.querySelectorAll('.equal-height')).forEach(

			(equalHeightContainer) => {

				this.instances.components.equalHeight.push(
					new EqualHeight(equalHeightContainer)
				);
			}
		);

		// Sticky Content Cards
		Array.from(document.querySelectorAll('.block-sticky-content-cards')).forEach((blockEl) => {
			this.instances.components.stickyContentCards.push(new StickyContentCards(blockEl));
		});

		//Interactive Maps
		Array.from(document.querySelectorAll('.block-interactive-map')).forEach((mapBlock) => {
			new InteractiveMap(mapBlock);
		});		

		//Banner Slider
		Array.from(document.querySelectorAll('.banner-default__type-slider')).forEach((banner) => {
			const slider = banner.querySelector('.banner-default__slider');
			const nav = banner.querySelector('.custom-nav-container-banner');
			if (slider) {
				new BannerSlider(slider, nav);
			}
		});

		//Banner Stats Slider
		Array.from(document.querySelectorAll('.banner-default__stats-slider')).forEach((banner) => {
			const slider = banner.querySelector('.banner-default__stats');
			if (slider) {
				this.instances.components.statsSlider.push(new BannerStatsSlider(slider));
			}
		});

		//Post Tabs
		Array.from(document.querySelectorAll('.block-posts__tabs')).forEach(
			(tabsBlock) => {
				this.instances.components.tabs = 
					new PostTabs(tabsBlock);
			}
		);

		// Load More Cards - Posts
		Array.from(document.querySelectorAll('.js-block-posts')).forEach((block) => {
			new LoadMorePosts(block);
		});

		//Load More Cards - Tabs Posts
		Array.from(document.querySelectorAll('.tab-content')).forEach((tab) => {
			const postsBlock = tab.querySelector('.js-block-posts'); 
			if (postsBlock && postsBlock.querySelectorAll('.js-post-card').length > 0) {
				new LoadMorePosts(postsBlock);
			}
		});
		
	}

	initTemplates() {

		// banner cover video
		const resourceTemplateEl = document.querySelector('.post-type-archive-resource');

		if (resourceTemplateEl) {
			this.instances.templates.resourceTemplate = new ResourceTemplate(resourceTemplateEl);
		}

		const eventTemplateEl = document.querySelector('.post-type-archive-event');

		if (eventTemplateEl) {
			this.instances.templates.eventTemplate = new EventTemplate(eventTemplateEl);
		}

	}

	async initBlocks() {

		// Accordion Image
		Array.from(document.querySelectorAll('.block-accordion')).forEach(
			(accordionImage) => {
				this.instances.blocks.accordionImage.push(
					new AccordionImage(accordionImage)
				);
			}
		);

		// Side Tabs
		Array.from(document.querySelectorAll('.block-side-tabs')).forEach(
			(sideTabs) => {
				this.instances.blocks.sideTabs.push(
					new SideTabs(sideTabs)
				);
			}
		);

		// setup block animation watchers
		const animateBlocks = document.querySelectorAll('.block-animate');

		if (animateBlocks && animateBlocks.length) {
			try {
				await loadIntersectionObserver();

				animateBlocks.forEach((watchEl) =>
					createSingleUseObserver(watchEl, (entry, el) => {
						el.classList.remove('block-animate');
					})
				);
			} catch (err) {
				console.error(err);
			}
		}
	}

	initSocialShare() {
		const socialShareEls = document.querySelectorAll('[data-social-share]');

		Array.from(socialShareEls).forEach((el) => {
			el.addEventListener('click', (e) => {
				const site = el.getAttribute('data-social-share');
				const shareUrl = el.getAttribute('href');

				e.preventDefault();

				if (!shareUrl) {
					return;
				}

				window.open(shareUrl, `${site}Share`, 'width=626,height=436');
			});
		});
	}
}

export default App;
